import {Component} from "@angular/core";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {DialogComponent} from "../../bootstrap-modal/dialog.component";
import "rxjs/add/operator/toPromise";
import {Observable} from "rxjs";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {toggleLoading} from "../../misc/loading.decorator";
import {SearchHelpers} from "../../helpers/search-helper";
import {PurchaseOrderService} from "../../purchase-order/purchase-order.service";
import {PurchaseOrdersCreateDialogComponent} from "./purchase-orders-create-dialog.component";
import {PurchaseOrderModel, purchaseOrderModelFactory} from "../../purchase-order/purchase-order.model";
import * as uuid from "uuid";

export interface SelectDialogPurchaseOrderModel extends PurchaseOrderModel{
  selected: boolean;
  disabled: boolean;
  uniqueId: string;
}

export interface SelectPurchaseOrdersDialogModel {
  purchaseOrders: SelectDialogPurchaseOrderModel[];
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;

@Component({
  templateUrl: "./purchase-orders-select-dialog.template.pug",
  providers: [
    PurchaseOrderService,
  ],
})
export class PurchaseOrdersSelectDialogComponent
  extends DialogComponent<SelectPurchaseOrdersDialogModel, any> implements SelectPurchaseOrdersDialogModel {
  purchaseOrders: SelectDialogPurchaseOrderModel[] = [];
  purchaseOrdersFiltered: SelectDialogPurchaseOrderModel[] = [];
  purchaseOrdersToShow: SelectDialogPurchaseOrderModel[] = [];
  selectedPurchaseOrderUniqueId: string = "";
  disableAddNewPOButton: boolean;
  public newPurchaseOrders: PurchaseOrderModel[] = [];
  public error: Error;
  loading: boolean = true;
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number = 1;
  searchText: string = "";

  static showSelectPurchaseOrdersDialogModal(
    dialogService: DialogService,
    inputs: SelectPurchaseOrdersDialogModel,
  ): Observable<any> {
    return dialogService.addDialog(
      PurchaseOrdersSelectDialogComponent,
      inputs,
      {backdropColor: "rgba(0, 0, 0, 0.5)"},
    );
  }

  cancel(): void {
    this.result = {
      selectedPurchaseOrder: null,
      newPurchaseOrders: null,
    };
    this.close();
  }

  @handleErrors()
  async save(): Promise<void> {
    this.result = {
      selectedPurchaseOrder: purchaseOrderModelFactory(
        this.purchaseOrders.find(po => po.uniqueId === this.selectedPurchaseOrderUniqueId),
      ),
      newPurchaseOrders: this.newPurchaseOrders,
    };
    this.close();
  }

  @toggleLoading()
  @handleErrors()
  async ready() {
    const selectedPurchaseOrder = this.purchaseOrders.find(po => !!po.selected);
    this.selectedPurchaseOrderUniqueId = selectedPurchaseOrder ? selectedPurchaseOrder.uniqueId : "";
    // Initial Load the PO's to the Grid List
    this.loadFilteredPurchaseOrders();
  }

  private loadFilteredPurchaseOrders(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.purchaseOrdersFiltered = this.purchaseOrders.filter((po) => {
      if (this.searchText) {
        const values = [
          po.purchaseOrderNumber,
          po.purchaseOrderDescription,
        ].filter(it => !!it);

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }
      return true;
    }).sort((a, b) => {
      if (a.purchaseOrderDescription > b.purchaseOrderDescription) return 1;
      if (b.purchaseOrderDescription > a.purchaseOrderDescription) return -1;
      if (a.createdOn > b.createdOn) return -1;
      if (b.createdOn > a.createdOn) return 1;
      return 0;
    });
    this.purchaseOrdersToShow = [
      ...this.purchaseOrdersFiltered.filter(it => it.uniqueId === this.selectedPurchaseOrderUniqueId),
      ...(this.purchaseOrdersFiltered.slice(start, end).filter(
        it => it.uniqueId !== this.selectedPurchaseOrderUniqueId, // Prevent duplicates in detail display
      ))];
    this.maxPages = Math.ceil(this.purchaseOrdersFiltered.length / (+this.pageSize));
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.loadFilteredPurchaseOrders();
  }

  async addNewPO() {
    await PurchaseOrdersCreateDialogComponent.showCreatePurchaseOrdersDialogModal(
      this.dialogService,
      {purchaseOrders: this.purchaseOrders},
    ).subscribe((it) => {
      if (it.purchaseOrderDescription && it.purchaseOrderNumber) {
        const newPurchaseOrder = purchaseOrderModelFactory({
          purchaseOrderNumber: it.purchaseOrderNumber,
          purchaseOrderDescription: it.purchaseOrderDescription,
          pendingPO: it.pendingPO,
          createdOn: Date.now(),
        });
        const uniqueId = uuid.v1();
        // Add the new purchase order into the list of orders
        this.purchaseOrders.push({...newPurchaseOrder, selected: true, disabled: false, uniqueId});
        // Add the new purchase order into the new purchase Orders List
        this.newPurchaseOrders.push(newPurchaseOrder);
        // Mark the new purchase order as selected
        this.selectedPurchaseOrderUniqueId = uniqueId;
        // Disable the Add new PO Button (we just allowed one new)
        this.disableAddNewPOButton = true;
        // Update the Grid
        this.loadFilteredPurchaseOrders();
      }
    });
  }

  changePurchaseOrderSelection(purchaseOrderUniqueId): void {
    this.selectedPurchaseOrderUniqueId = purchaseOrderUniqueId;
    this.loadFilteredPurchaseOrders();
  }

  constructor(
    public dialogService: DialogService,
  ) {
    super(dialogService);
  }
}
